import { type CareerProfile } from 'CareerProfiles';
import { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { type AnyObject } from '@Types';
import { promiseCacheFactory } from 'PromiseCacheFactory';
import deepEqual from 'deep-equal';
import { type SerializedUser } from '../messaging.SerializedUser.types';
import { getSerializedUsersToMessage } from '../selectors';
import { setUsersToMessage } from '../actions';
import { useCreateUserPromiseFromIds } from './useCreateUserPromiseFromIds';

const USERS_CACHE_KEY = 'messaging-users-to-message';

function cacheKey(userId: string, cohortId: string | null) {
    return `${USERS_CACHE_KEY}:${userId}:${cohortId ?? 'none'}`;
}

function getCachedUsersToMessage(userId: string, cohortId: string | null) {
    try {
        const raw = sessionStorage.getItem(cacheKey(userId, cohortId));
        if (!raw) return null;
        return JSON.parse(raw) as {
            availableUsers: AnyObject[];
            suggestedUsers: AnyObject[];
            profilesById: AnyObject<CareerProfile>;
        };
    } catch {
        return null;
    }
}

function setCachedUsersToMessage(
    userId: string,
    cohortId: string | null,
    data: { availableUsers: AnyObject[]; suggestedUsers: AnyObject[]; profilesById: AnyObject<CareerProfile> },
) {
    try {
        sessionStorage.setItem(cacheKey(userId, cohortId), JSON.stringify(data));
    } catch {
        // sessionStorage full or unavailable
    }
}

const [promiseCache] = promiseCacheFactory();

const useCreateUsersToMessagePromise = ({
    userIds,
    recommendedProfiles,
    usersWithExistingMessages,
    allProfiles,
}: {
    userIds: string[];
    recommendedProfiles: CareerProfile[];
    usersWithExistingMessages: string[];
    allProfiles: CareerProfile[];
}) => {
    const dispatch = useDispatch();

    const createUserPromiseArray = useCreateUserPromiseFromIds();

    const createUsersToMessagePromise = useCallback(
        () =>
            Promise.all(createUserPromiseArray(userIds)).then((...userArraysArray) => {
                const users = userArraysArray.flat().flatMap(a => a.map(u => u));

                const updatedUsersToMessage = {
                    availableUsers: users
                        .map(user => (userIds.find(userId => userId === user.userId) ? user.serialize() : null))
                        .filter(v => !!v) as AnyObject[],

                    suggestedUsers: [
                        // combine the recommended users and the users we already have messages
                        // with to create the "suggested users"
                        ...new Set(recommendedProfiles.map(p => p.userId).concat(usersWithExistingMessages)).values(),
                    ]
                        .map(id => users.find(u => u.userId === id)?.serialize() || null)
                        .filter(v => !!v) as AnyObject[],
                    profilesById: allProfiles.reduce<AnyObject<CareerProfile>>(
                        (prev, curr) => ({ ...prev, [curr.userId]: curr }),
                        {},
                    ),
                };

                dispatch(setUsersToMessage(updatedUsersToMessage));
            }),
        [allProfiles, createUserPromiseArray, dispatch, recommendedProfiles, userIds, usersWithExistingMessages],
    );

    return createUsersToMessagePromise;
};

// oxlint-disable-next-line max-lines-per-function -- caching logic adds lines
export const useGetUsersAndCareerProfiles = ({
    recommendedProfiles,
    relevantUserProfiles,
    usersWithExistingMessages,
    currentUserId,
    cohortId,
}: {
    recommendedProfiles: CareerProfile[];
    relevantUserProfiles: CareerProfile[];
    usersWithExistingMessages: string[];
    currentUserId: string;
    cohortId: string | null;
}) => {
    const dispatch = useDispatch();
    const serializedUsersToMessage = useSelector(getSerializedUsersToMessage, deepEqual);

    // Use serialized (plain object) data directly for display — no expensive
    // sdk.buildUserFromSerializedData calls. The serialized data already has
    // userId, nickname, plainProfileUrl, connectionStatus, etc. Only deserialize
    // via sdk.buildUserFromSerializedData when actually creating a Sendbird channel.
    const usersToMessage = useMemo(
        () => ({
            availableUsers: (serializedUsersToMessage?.availableUsers || []) as SerializedUser[],
            suggestedUsers: (serializedUsersToMessage?.suggestedUsers || []) as SerializedUser[],
            profilesById: serializedUsersToMessage?.profilesById || {},
        }),
        [
            serializedUsersToMessage?.availableUsers,
            serializedUsersToMessage?.profilesById,
            serializedUsersToMessage?.suggestedUsers,
        ],
    );

    const allProfiles = useMemo(() => {
        const seen = new Set<string>();
        return relevantUserProfiles.concat(recommendedProfiles).filter(p => {
            if (seen.has(p.userId) || !p.messagingEnabled || !p.hasEverHadSendbirdAccess) return false;
            seen.add(p.userId);
            return true;
        });
    }, [recommendedProfiles, relevantUserProfiles]);

    const userIds = useMemo(() => allProfiles.map(cp => cp.userId), [allProfiles]);

    const createUsersToMessagePromise = useCreateUsersToMessagePromise({
        userIds,
        recommendedProfiles,
        usersWithExistingMessages,
        allProfiles,
    });

    if (!serializedUsersToMessage) {
        // Try sessionStorage cache for instant display, then refresh in background
        const cached = getCachedUsersToMessage(currentUserId, cohortId);
        if (cached) {
            dispatch(setUsersToMessage(cached));
            // Refresh in the background — createUsersToMessagePromise dispatches
            // to Redux when done, triggering a re-render with fresh data
            createUsersToMessagePromise();
        } else {
            // No cache — suspend while fetching
            const promise = promiseCache(JSON.stringify(userIds.sort()), createUsersToMessagePromise());
            throw promise;
        }
    }

    // Keep sessionStorage in sync — only write when data reference changes, not every render
    const prevDataRef = useRef(serializedUsersToMessage);
    if (serializedUsersToMessage && serializedUsersToMessage !== prevDataRef.current) {
        prevDataRef.current = serializedUsersToMessage;
        setCachedUsersToMessage(currentUserId, cohortId, serializedUsersToMessage);
    }

    return usersToMessage;
};
